/**
 * DocumentDetailsPage Component
 * 
 * A comprehensive React component for displaying and managing individual document details.
 * Features a split-view layout with metadata panel on the left and document preview on the right.
 * 
 * Features:
 * - Split-view layout (metadata + preview)
 * - Document metadata display and editing
 * - Related items management
 * - Document preview functionality
 * - Action buttons (edit, download, delete, etc.)
 * - Association management
 * 
 * URL Parameters:
 * - tripId: The ID of the parent trip
 * - documentId: The ID of the specific document
 * 
 * Authentication:
 * - Requires authenticated user (via AuthContext)
 * - User-specific data isolation
 * 
 * @component
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  useAuth,
  useTrip,
  LinkEntitiesButton,
  WaitingIndicator,
  linkPresentationService,
  eventBridgeService,
  EVENT_TYPES
} from '@trip-planner-monorepo/shared';
import { documentService } from '../services/documentService';
import { configService } from '../services/configService';
import { aiService } from '../services/aiService';
import { documentEntityMappingService } from '../services/documentEntityMappingService';
import { daysService, calculateDaysListDateRange } from '@trip-planner-monorepo/days';
import ManageAssociationsModal from './ManageAssociationsModal';
// Entity creation components - lazy loaded when needed
import AddTravel from '@trip-planner-monorepo/travel/components/AddTravel/AddTravel';
import AddAccommodation from '@trip-planner-monorepo/accommodation/components/AddAccommodation/AddAccommodation';
import {
  FaEdit,
  FaDownload,
  FaTrash,
  FaExternalLinkAlt,
  FaLink,
  FaCalendarAlt,
  FaFileAlt,
  FaHotel,
  FaMapMarkerAlt,
  FaPlane,
  FaBus,
  FaTrain,
  FaCar,
  FaArrowLeft,
  FaTags,
  FaUser,
  FaWeight,
  FaClock,
  FaPlus,
  FaMagic,
  FaExclamationTriangle
} from 'react-icons/fa';
import './DocumentDetailsPage.css';


function DocumentDetailsPage() {
  // State Management
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedCategory, setEditedCategory] = useState('');
  const [showManageAssociations, setShowManageAssociations] = useState(false);

  // Entity Creation State
  const [availableEntityTypes, setAvailableEntityTypes] = useState([]);
  const [selectedEntityType, setSelectedEntityType] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedEntityData, setExtractedEntityData] = useState(null);
  const [showEntityForm, setShowEntityForm] = useState(false);
  const [entityFormType, setEntityFormType] = useState(null); // 'travel' or 'accommodation'
  const [registryRelatedItems, setRegistryRelatedItems] = useState([]);
  const [registryRelatedVersion, setRegistryRelatedVersion] = useState(0);

  // Context and Router Hooks
  const { currentUser } = useAuth();
  const { effectiveUserId } = useTrip();
  const { tripId, documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Fetches document details when component mounts
   */
  useEffect(() => {
    if (!currentUser || !tripId || !documentId) return;

    fetchDocumentDetails();
  }, [currentUser, tripId, documentId]);

  const loadRegistryRelatedItems = useCallback(async () => {
    if (!effectiveUserId || !tripId || !documentId) {
      setRegistryRelatedItems([]);
      return;
    }

    try {
      const { source, models } = await linkPresentationService.getRelatedEntityModels(
        effectiveUserId,
        tripId,
        'document',
        documentId,
        { surface: 'related' }
      );
      if (source === 'registry') {
        setRegistryRelatedItems((models || []).filter((model) => model.entityType !== 'document'));
      } else {
        setRegistryRelatedItems([]);
      }
    } catch (err) {
      console.error('[DocumentDetailsPage] Failed to load registry related items:', err);
      setRegistryRelatedItems([]);
    }
  }, [effectiveUserId, tripId, documentId]);

  useEffect(() => {
    loadRegistryRelatedItems();
  }, [loadRegistryRelatedItems, registryRelatedVersion]);

  useEffect(() => {
    if (!effectiveUserId || !tripId) {
      return undefined;
    }

    const linkEvents = [
      EVENT_TYPES.LINK_CREATED,
      EVENT_TYPES.LINK_UPDATED,
      EVENT_TYPES.LINK_REMOVED
    ];
    const subscriptions = linkEvents.map((eventType) =>
      eventBridgeService.subscribe(eventType, (payload) => {
        if (payload?.tripId && payload.tripId !== tripId) {
          return;
        }
        setRegistryRelatedVersion((version) => version + 1);
      })
    );

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, [effectiveUserId, tripId]);

  const displayRelatedItems = useMemo(() => {
    const merged = [];
    const seen = new Set();

    (registryRelatedItems || []).forEach((model) => {
      const key = `${model.entityType}:${model.entityId}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      merged.push({
        key: `registry-${key}`,
        entityType: model.entityType,
        entityId: model.entityId,
        entityName: model.entityName,
        connectionType: 'related'
      });
    });

    (document?.connections || []).forEach((connection, index) => {
      if (!connection?.entityType || !connection?.entityId) {
        return;
      }
      const key = `${connection.entityType}:${connection.entityId}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      merged.push({
        key: `legacy-${key}-${index}`,
        entityType: connection.entityType,
        entityId: connection.entityId,
        entityName: connection.entityName,
        connectionType: connection.connectionType || 'related'
      });
    });

    return merged;
  }, [document?.connections, registryRelatedItems]);

  /**
   * Update available entity types when document changes
   */
  useEffect(() => {
    if (document?.primaryCategory) {
      const types = documentEntityMappingService.getAvailableEntityTypes(document.primaryCategory);
      setAvailableEntityTypes(types);

      // Set default selected type to the suggested one
      const suggested = documentEntityMappingService.getSuggestedEntityType(document.primaryCategory);
      if (suggested && documentEntityMappingService.isEntityTypeEnabled(suggested.id)) {
        setSelectedEntityType(suggested.id);
      }
    }
  }, [document?.primaryCategory]);

  /**
   * Fetches document details from the service
   */
  const fetchDocumentDetails = async () => {
    try {
      setLoading(true);
      const docData = await documentService.getDocument(effectiveUserId, tripId, documentId);

      if (!docData) {
        setError('Document not found.');
        return;
      }

      setDocument(docData);
      setEditedTitle(docData.title || docData.name || '');
      setEditedCategory(docData.primaryCategory || 'uncategorized');
    } catch (err) {
      setError('Failed to load document details. Please try again.');
      console.error('Error fetching document:', err);
    } finally {
      setLoading(false);
    }
  };


  /**
   * Handles title editing
   */
  const handleSaveTitle = async () => {
    if (!editedTitle.trim()) {
      setError('Title cannot be empty');
      return;
    }

    try {
      await documentService.updateDocument(effectiveUserId, tripId, documentId, {
        title: editedTitle.trim(),
        name: editedTitle.trim() // Keep name field in sync with title for backward compatibility
      });

      setDocument(prev => ({
        ...prev,
        title: editedTitle.trim(),
        name: editedTitle.trim()
      }));
      setIsEditingTitle(false);
      setError('');
    } catch (err) {
      setError('Failed to update title. Please try again.');
      console.error('Error updating title:', err);
    }
  };

  /**
   * Handles category editing
   */
  const handleSaveCategory = async () => {
    try {
      await documentService.updateDocument(effectiveUserId, tripId, documentId, {
        primaryCategory: editedCategory,
        categories: [
          {
            id: editedCategory,
            confidence: 1.0,
            isPrimary: true
          }
        ]
      });

      setDocument(prev => ({
        ...prev,
        primaryCategory: editedCategory,
        categories: [{ id: editedCategory, confidence: 1.0, isPrimary: true }]
      }));
      setIsEditingCategory(false);
      setError('');
    } catch (err) {
      setError('Failed to update category. Please try again.');
      console.error('Error updating category:', err);
    }
  };

  /**
   * Handles document download
   */
  const handleDownload = () => {
    if (document?.downloadUrl) {
      window.open(document.downloadUrl, '_blank');
    }
  };

  /**
   * Handles document deletion
   */
  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      await documentService.deleteDocument(effectiveUserId, tripId, documentId);
      navigate(`/trips/${tripId}/documents`);
    } catch (err) {
      setError('Failed to delete document. Please try again.');
      console.error('Error deleting document:', err);
    }
  };

  /**
   * Handles creating a new entity from this document
   * Triggers on-demand AI extraction and opens the appropriate form
   */
  const handleCreateNew = async () => {
    if (!selectedEntityType) {
      setError('Please select an entity type');
      return;
    }

    const entityConfig = documentEntityMappingService.getEntityType(selectedEntityType);
    if (!entityConfig || !entityConfig.enabled) {
      setError('Selected entity type is not currently available');
      return;
    }

    try {
      setIsExtracting(true);
      setError('');

      // Fetch trip date context for better AI date inference
      let tripContext = null;
      try {
        const days = await daysService.fetchDays(effectiveUserId, tripId);
        const dateRange = calculateDaysListDateRange(days);
        if (dateRange) {
          tripContext = {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
          };
          console.log('Trip context for AI extraction:', tripContext);
        }
      } catch (err) {
        console.warn('Could not fetch trip dates for AI context:', err);
        // Continue without trip context - AI will use fallback logic
      }

      // Fetch the document file for AI extraction
      const response = await fetch(document.downloadUrl);
      const blob = await response.blob();
      const file = new File([blob], document.name || 'document', { type: document.mimeType || blob.type });

      // Perform entity-specific extraction with trip context
      const extractionResult = await aiService.extractDataForEntity(file, selectedEntityType, tripContext);

      if (!extractionResult.success) {
        setError(`Failed to extract data: ${extractionResult.error}`);
        setIsExtracting(false);
        return;
      }

      // Map extracted data to form format
      const formData = documentEntityMappingService.mapToFormData(extractionResult.data, selectedEntityType);
      setExtractedEntityData(formData);

      // Determine which form to show
      const formComponent = entityConfig.formComponent;
      setEntityFormType(formComponent === 'AddTravel' ? 'travel' : 'accommodation');
      setShowEntityForm(true);
    } catch (err) {
      console.error('Error during entity creation:', err);
      setError('Failed to extract data from document. Please try again.');
    } finally {
      setIsExtracting(false);
    }
  };

  /**
   * Handles when an entity is successfully saved from the form
   * Saves the entity to Firestore, then creates a link between document and new entity
   */
  const handleEntitySaved = async (entityData) => {
    try {
      console.log('handleEntitySaved called with:', entityData);

      let savedEntityId = null;
      let entityName = '';

      // Determine which service to use based on entity form type
      if (entityFormType === 'travel') {
        // Import and use travelService to save the travel item
        const { travelService } = await import('@trip-planner-monorepo/travel/services/travelService');

        // Save the travel item and get the ID
        // Note: createTravel -> addTravel returns the document ID as a string, not an object
        const savedTravelId = await travelService.createTravel(effectiveUserId, tripId, entityData);

        // addTravel returns docRef.id which is a string
        if (typeof savedTravelId === 'string') {
          savedEntityId = savedTravelId;
        } else if (savedTravelId && savedTravelId.id) {
          // Fallback for if return type changes
          savedEntityId = savedTravelId.id;
        }

        entityName = entityData.summary ||
          `${selectedEntityType.charAt(0).toUpperCase() + selectedEntityType.slice(1)}` ||
          `New ${selectedEntityType}`;

        console.log('Travel saved with ID:', savedEntityId);

      } else if (entityFormType === 'accommodation') {
        // Import and use accommodationService to save the accommodation
        const { accommodationService } = await import('@trip-planner-monorepo/accommodation/services/accommodationService');

        // Save the accommodation and get the ID
        // Note: createAccommodation -> addAccommodation returns the document ID as a string
        const savedAccommodationId = await accommodationService.createAccommodation(effectiveUserId, tripId, entityData);

        // addAccommodation returns docRef.id which is a string
        if (typeof savedAccommodationId === 'string') {
          savedEntityId = savedAccommodationId;
        } else if (savedAccommodationId && savedAccommodationId.id) {
          // Fallback for if return type changes
          savedEntityId = savedAccommodationId.id;
        }

        entityName = entityData.details?.name || entityData.hotelName || entityData.summary || `New ${selectedEntityType}`;

        console.log('Accommodation saved with ID:', savedEntityId);
      }


      // If we didn't get an ID, something went wrong
      if (!savedEntityId) {
        throw new Error('Failed to get ID of saved entity');
      }

      // Add connection from document to new entity
      const connection = {
        entityType: selectedEntityType,
        entityId: savedEntityId,
        entityName: entityName,
        connectionType: 'primary',
        associatedAt: new Date().toISOString()
      };

      await documentService.addConnection(effectiveUserId, tripId, documentId, connection);

      // Update local state
      setDocument(prev => ({
        ...prev,
        connections: [...(prev.connections || []), connection]
      }));

      // Close the form
      setShowEntityForm(false);
      setExtractedEntityData(null);
      setError('');

      console.log('Entity created and linked successfully:', savedEntityId);
    } catch (err) {
      console.error('Error saving entity or linking document:', err);
      setError(`Failed to save: ${err.message}`);
    }
  };


  /**
   * Closes the entity creation form
   */
  const handleCloseEntityForm = () => {
    setShowEntityForm(false);
    setExtractedEntityData(null);
  };

  /**
   * Gets the appropriate icon for an entity type
   * @param {string} entityType - The type of entity
   * @returns {JSX.Element} Icon component
   */
  const getEntityIcon = (entityType) => {
    switch (entityType?.toLowerCase()) {
      case 'day':
        return <FaCalendarAlt className="entity-icon day-icon" />;
      case 'accommodation':
        return <FaHotel className="entity-icon accommodation-icon" />;
      case 'place':
        return <FaMapMarkerAlt className="entity-icon place-icon" />;
      case 'travel':
        return <FaPlane className="entity-icon travel-icon" />;
      case 'flight':
        return <FaPlane className="entity-icon flight-icon" />;
      case 'train':
        return <FaTrain className="entity-icon train-icon" />;
      case 'bus':
        return <FaBus className="entity-icon bus-icon" />;
      case 'car':
        return <FaCar className="entity-icon car-icon" />;
      default:
        return <FaLink className="entity-icon generic-icon" />;
    }
  };

  /**
   * Builds navigation URL for an entity
   * @param {Object} connection - Connection object
   * @returns {string} Navigation URL
   */
  const getEntityUrl = (connection) => {
    const { entityType, entityId } = connection;
    const baseUrl = `/trips/${tripId}`;

    switch (entityType?.toLowerCase()) {
      case 'day':
        return `${baseUrl}/day/${entityId}`;
      case 'accommodation':
      case 'hotel':
      case 'hostel':
      case 'apartment':
      case 'villa':
      case 'resort':
      case 'guesthouse':
      case 'camping':
        return `${baseUrl}/accommodation/${entityId}`;
      case 'place':
        return `${baseUrl}/places/${entityId}`;
      case 'travel':
      case 'flight':
      case 'train':
      case 'bus':
      case 'car':
        return `${baseUrl}/travel/${entityId}`;
      default:
        console.warn(`getEntityUrl: Unknown entityType "${entityType}", defaulting to base URL`);
        return baseUrl;
    }
  };

  /**
   * Handles associations update from ManageAssociationsModal
   */
  const handleAssociationsUpdated = (updatedAssociations) => {
    // Convert associations to connections format
    const updatedConnections = updatedAssociations.map(association => ({
      entityType: association.entityType,
      entityId: association.entityId,
      entityName: association.entityName,
      connectionType: association.connectionType || 'related',
      associatedAt: association.selectedAt || new Date().toISOString()
    }));

    // Update the document state with new connections
    setDocument(prev => ({
      ...prev,
      connections: updatedConnections
    }));

    setRegistryRelatedVersion((version) => version + 1);
    setError(''); // Clear any errors
  };

  /**
   * Formats file size for display
   */
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  /**
   * Formats date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Gets category display information
   */
  const getCategoryInfo = (categoryId) => {
    const taxonomy = configService.getTaxonomy();
    const category = taxonomy.categories.find(cat => cat.id === categoryId);
    return category || { id: categoryId, name: categoryId, icon: '📄' };
  };

  // Loading state
  if (loading) {
    return <div className="document-details-loading">Loading document details...</div>;
  }

  // Error state
  if (error && !document) {
    return <div className="document-details-error">{error}</div>;
  }

  // Document not found
  if (!document) {
    return <div className="document-details-error">Document not found.</div>;
  }

  const categoryInfo = getCategoryInfo(document.primaryCategory);

  return (
    <div className="document-details-page">
      {/* Header with navigation */}
      <div className="document-details-header">
        {location.state?.fromEntity && location.state?.returnPath ? (
          <button
            onClick={() => navigate(location.state.returnPath)}
            className="back-link"
            title="Return to previous page"
          >
            <FaArrowLeft /> Back
          </button>
        ) : (
          <Link to={`/trips/${tripId}/documents`} className="back-link">
            ← Back to Documents
          </Link>
        )}
        <div className="document-header-content">
          <h1>{document.title || document.name}</h1>
          <LinkEntitiesButton
            className="edit-button"
            onClick={() => setShowManageAssociations(true)}
          />
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Premium Waiting Indicator for AI Extraction */}
      {isExtracting && (
        <WaitingIndicator
          fullPage={true}
          message={`AI is analyzing your ${categoryInfo.name} document...`}
        />
      )}

      {/* Split view layout */}
      <div className="document-details-content">
        {/* Left Panel: Metadata */}
        <div className="document-metadata-panel">
          <div className="metadata-section">
            <h2>📋 Metadata</h2>

            {/* Title */}
            <div className="metadata-item">
              <label>Title:</label>
              {isEditingTitle ? (
                <div className="edit-field">
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSaveTitle()}
                    autoFocus
                  />
                  <button onClick={handleSaveTitle} className="save-btn">Save</button>
                  <button onClick={() => setIsEditingTitle(false)} className="cancel-btn">Cancel</button>
                </div>
              ) : (
                <div className="display-field">
                  <span>{document.title || document.name}</span>
                  <button onClick={() => setIsEditingTitle(true)} className="edit-icon">
                    <FaEdit />
                  </button>
                </div>
              )}
            </div>

            {/* Original Name */}
            <div className="metadata-item">
              <label>Original Name:</label>
              <span>{document.originalName || document.name}</span>
            </div>

            {/* Category */}
            <div className="metadata-item">
              <label>Category:</label>
              {isEditingCategory ? (
                <div className="edit-field">
                  <select
                    value={editedCategory}
                    onChange={(e) => setEditedCategory(e.target.value)}
                  >
                    {configService.getTaxonomy().categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <button onClick={handleSaveCategory} className="save-btn">Save</button>
                  <button onClick={() => setIsEditingCategory(false)} className="cancel-btn">Cancel</button>
                </div>
              ) : (
                <div className="display-field">
                  <span>{categoryInfo.icon} {categoryInfo.name}</span>
                  <button onClick={() => setIsEditingCategory(true)} className="edit-icon">
                    <FaEdit />
                  </button>
                </div>
              )}
            </div>

            {/* Upload Date */}
            <div className="metadata-item">
              <label><FaClock /> Uploaded:</label>
              <span>{formatDate(document.uploadedAt)}</span>
            </div>

            {/* File Size */}
            <div className="metadata-item">
              <label><FaWeight /> Size:</label>
              <span>{formatFileSize(document.size)}</span>
            </div>

            {/* Processing Status */}
            <div className="metadata-item">
              <label>Status:</label>
              <span className={`status-badge status-${document.processingStatus}`}>
                {document.processingStatus || 'Unknown'}
              </span>
            </div>
          </div>

          {/* Create From Document Section */}
          {availableEntityTypes.length > 0 && document.processingStatus === 'completed' && (
            <div className="create-entity-section">
              <h2><FaMagic /> Create From This Document</h2>
              <p className="create-entity-description">
                AI categorized this as: <strong>{categoryInfo.name}</strong>.
                You can create a new item pre-filled with extracted data.
              </p>

              <div className="entity-type-selector">
                <label htmlFor="entityTypeSelect">Create as:</label>
                <select
                  id="entityTypeSelect"
                  value={selectedEntityType}
                  onChange={(e) => setSelectedEntityType(e.target.value)}
                  disabled={isExtracting}
                >
                  {availableEntityTypes.map(category => (
                    <optgroup key={category.id} label={`${category.icon} ${category.label}`}>
                      {category.types.map(type => (
                        <option
                          key={type.id}
                          value={type.id}
                          disabled={!type.enabled}
                        >
                          {type.icon} {type.label}
                          {type.isSuggested ? ' (suggested)' : ''}
                          {!type.enabled ? ' (coming soon)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="create-entity-actions">
                <button
                  onClick={handleCreateNew}
                  disabled={isExtracting || !selectedEntityType}
                  className="btn-primary create-entity-btn"
                  title="Creates one entity at a time. Repeat for additional items."
                >
                  <FaPlus /> {isExtracting ? 'Extracting...' : 'Create New'}
                </button>
                <button
                  disabled
                  className="btn-secondary update-entity-btn"
                  title="Update an existing item with data from this document (coming soon)"
                >
                  Update Existing... (coming soon)
                </button>
              </div>
            </div>
          )}

          {/* Related Items Section */}

          <div className="related-items-section">
            <h2>🔗 Related Items</h2>
            {displayRelatedItems.length > 0 ? (
              <div className="related-items-list">
                {displayRelatedItems.map((connection) => (
                  <Link
                    key={connection.key}
                    to={getEntityUrl(connection)}
                    state={{
                      previousBreadcrumbs: [
                        { name: 'Trips', path: '/', isHome: true, isCurrent: false },
                        { name: 'Days', path: `/trips/${tripId}`, isTrip: true, isCurrent: false },
                        { name: 'Documents', path: `/trips/${tripId}/documents`, isCurrent: false },
                        { name: document?.title || 'Document', path: `/trips/${tripId}/documents/${documentId}`, isCurrent: false }
                      ]
                    }}
                    className="related-item-link"
                    title={`Navigate to ${connection.entityType}: ${connection.entityName || 'Unnamed'}`}
                  >
                    <div className="related-item">
                      {getEntityIcon(connection.entityType)}
                      <div className="related-item-content">
                        <span className="connection-type">
                          {connection.entityType?.charAt(0).toUpperCase() + connection.entityType?.slice(1) || 'Entity'}
                        </span>
                        <span className="connection-name">
                          {connection.entityName || 'Unnamed Item'}
                        </span>
                        {connection.connectionType && (
                          <span className="connection-badge">
                            {connection.connectionType === 'primary' ? 'Primary' : 'Related'}
                          </span>
                        )}
                      </div>
                      <FaExternalLinkAlt className="nav-arrow" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="no-related-items">No related items</p>
            )}
            <LinkEntitiesButton
              onClick={() => setShowManageAssociations(true)}
              text="Manage Associations"
              className="manage-associations-btn"
            />
          </div>

          {/* Summary Section */}
          {document.summary && (
            <div className="summary-section">
              <h2>📝 Summary</h2>
              <p>{document.summary}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons">
            <button onClick={handleDownload} className="btn-primary">
              <FaDownload /> Download
            </button>
            <button onClick={handleDownload} className="btn-secondary">
              <FaExternalLinkAlt /> View original document
            </button>
            <button onClick={handleDelete} className="btn-danger">
              <FaTrash /> Delete
            </button>
          </div>
        </div>

        {/* Right Panel: Document Preview */}
        <div className="document-preview-panel">
          <div className="preview-header">
            <h2>Document Preview</h2>
            <button onClick={handleDownload} className="view-original-link">
              <FaExternalLinkAlt /> View original document
            </button>
          </div>
          <div className="preview-container">
            {document.preview && document.preview.content ? (
              <div className="preview-content">
                {document.preview.format === 'image/png' || document.preview.format === 'image/jpeg' ? (
                  <img
                    src={document.preview.content}
                    alt="Document preview"
                    className="preview-image"
                  />
                ) : document.preview.type === 'pdf' ? (
                  <img
                    src={document.preview.content}
                    alt="PDF preview"
                    className="preview-image"
                  />
                ) : document.preview.type === 'text' ? (
                  <div className="text-preview">
                    <pre className="text-preview-content">{document.preview.content}</pre>
                    {document.preview.metadata?.truncated && (
                      <div className="text-preview-note">
                        Preview shows first 500 characters. Full document: {document.preview.metadata.fullLength} characters.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="preview-placeholder">
                    <FaFileAlt size={64} />
                    <p>Preview not available for this document type</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="preview-placeholder">
                <FaFileAlt size={64} />
                <p>No preview available</p>
                <button onClick={handleDownload} className="preview-download-btn">
                  <FaDownload /> Download to View
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manage Associations Modal */}
      {showManageAssociations && (
        <ManageAssociationsModal
          document={document}
          onClose={() => setShowManageAssociations(false)}
          onAssociationsUpdated={handleAssociationsUpdated}
        />
      )}

      {/* Entity Creation Modals */}
      {showEntityForm && entityFormType === 'travel' && (
        <AddTravel
          isOpen={showEntityForm}
          onClose={handleCloseEntityForm}
          onSave={handleEntitySaved}
          initialTravel={{
            type: selectedEntityType,
            details: extractedEntityData
          }}
          warningBanner={extractedEntityData && (
            <div className="ai-extraction-warning">
              <FaExclamationTriangle className="warning-icon" />
              <div className="warning-content">
                <strong>AI-Extracted Data</strong>
                <p>
                  The fields below have been pre-filled from AI analysis of the document.
                  Please review the accuracy and completeness of the information before saving.
                </p>
              </div>
            </div>
          )}
        />
      )}

      {showEntityForm && entityFormType === 'accommodation' && (
        <AddAccommodation
          isOpen={showEntityForm}
          onClose={handleCloseEntityForm}
          onSave={handleEntitySaved}
          initialAccommodation={{
            type: selectedEntityType,
            details: extractedEntityData || {}
          }}
          warningBanner={extractedEntityData && (
            <div className="ai-extraction-warning">
              <FaExclamationTriangle className="warning-icon" />
              <div className="warning-content">
                <strong>AI-Extracted Data</strong>
                <p>
                  The fields below have been pre-filled from AI analysis of the document.
                  Please review the accuracy and completeness of the information before saving.
                </p>
              </div>
            </div>
          )}
        />
      )}
    </div>
  );
}

export default DocumentDetailsPage;
